/**
 * Auth API service — communicates with backend auth routes.
 *
 * Uses the shared apiClient (Axios instance with Supabase JWT auth
 * and automatic token refresh).
 */

import { apiClient } from './apiClient';
import type {
  UserProfile,
  Workspace,
  WorkspaceMember,
  CreateWorkspaceRequest,
  UpdateProfileRequest,
  WorkspaceInvitation,
  SendInvitationRequest,
  InvitationInfo,
} from '../types/auth';

/**
 * Fetch the authenticated user's profile (creates one if missing).
 */
export async function fetchProfile(): Promise<UserProfile> {
  const response = await apiClient.get<UserProfile>('/auth/me');
  return response.data;
}

/**
 * Update the authenticated user's profile.
 */
export async function updateProfile(data: UpdateProfileRequest): Promise<UserProfile> {
  const response = await apiClient.put<UserProfile>('/auth/me', data);
  return response.data;
}

/**
 * Remember the workspace the user just switched to (#1262), so a fresh load
 * restores it instead of defaulting to the first membership alphabetically.
 * Best-effort — callers fire-and-forget; a failure just means the next load
 * falls back to the previous default.
 */
export async function setLastActiveWorkspace(workspaceId: string): Promise<void> {
  await apiClient.put('/auth/me/last-active-workspace', { workspace_id: workspaceId });
}

/**
 * Fetch all workspaces the authenticated user belongs to.
 */
export async function fetchWorkspaces(): Promise<Workspace[]> {
  const response = await apiClient.get<Workspace[]>('/auth/workspaces');
  return response.data;
}

/**
 * Create a new workspace with the authenticated user as owner.
 */
export async function createWorkspace(data: CreateWorkspaceRequest): Promise<Workspace> {
  const response = await apiClient.post<Workspace>('/auth/workspaces', data);
  return response.data;
}

/**
 * Fetch a single workspace by ID.
 */
export async function fetchWorkspace(workspaceId: string): Promise<Workspace> {
  const response = await apiClient.get<Workspace>(`/auth/workspaces/${workspaceId}`);
  return response.data;
}

/**
 * Deactivate the authenticated user's account (soft delete).
 */
export async function deactivateAccount(): Promise<{ detail: string; workspaces_deactivated: number }> {
  const response = await apiClient.post<{ detail: string; workspaces_deactivated: number }>('/auth/me/deactivate');
  return response.data;
}

/**
 * Fetch members of a workspace.
 */
export async function fetchWorkspaceMembers(workspaceId: string): Promise<WorkspaceMember[]> {
  const response = await apiClient.get<WorkspaceMember[]>(
    `/auth/workspaces/${workspaceId}/members`
  );
  return response.data;
}

/**
 * Send a workspace invitation to an email address.
 */
export async function sendInvitation(
  workspaceId: string,
  data: SendInvitationRequest
): Promise<{ invite_url: string; email_sent: boolean }> {
  const response = await apiClient.post<{ invite_url: string; email_sent: boolean }>(
    `/auth/workspaces/${workspaceId}/invitations`,
    data
  );
  return response.data;
}

/**
 * List pending invitations for a workspace.
 */
export async function listInvitations(workspaceId: string): Promise<WorkspaceInvitation[]> {
  const response = await apiClient.get<WorkspaceInvitation[]>(
    `/auth/workspaces/${workspaceId}/invitations`
  );
  return response.data;
}

/**
 * Revoke a pending invitation by ID.
 */
export async function revokeInvitation(workspaceId: string, invitationId: string): Promise<void> {
  await apiClient.delete(`/auth/workspaces/${workspaceId}/invitations/${invitationId}`);
}

/**
 * Fetch deactivated workspaces the current user can recover (within 30 days).
 */
export async function fetchRecoverableWorkspaces(): Promise<
  { id: string; name: string; website?: string; deactivated_at?: string }[]
> {
  const response = await apiClient.get('/auth/workspaces/recoverable');
  return response.data;
}

/**
 * Recover a previously deactivated workspace.
 */
export async function recoverWorkspace(workspaceId: string): Promise<Workspace> {
  const response = await apiClient.post<Workspace>(`/auth/workspaces/${workspaceId}/recover`);
  return response.data;
}

/**
 * Get public invitation info by token (no auth required).
 */
export async function getInvitationInfo(token: string): Promise<InvitationInfo> {
  const response = await apiClient.get<InvitationInfo>(`/auth/invitations/${token}`);
  return response.data;
}

/**
 * Accept a workspace invitation (requires authentication).
 */
export async function acceptInvitation(
  token: string
): Promise<{ workspace_id: string; workspace_name: string; role: string }> {
  const response = await apiClient.post<{ workspace_id: string; workspace_name: string; role: string }>(
    `/auth/invitations/${token}/accept`
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Admin Panel types (elevated platform roles only)
// ---------------------------------------------------------------------------

/**
 * The Teamwork person matched to a Users-tab row by email (O41/O42). Read-only
 * department; the email itself is never part of this block.
 */
export interface InternalMember {
  teamwork_person_id: string;
  name: string;
  active: boolean;
  display_name_override: string | null;
  note: string | null;
  not_in_teamwork: boolean;
  updated_by: string | null;
  updated_at: string | null;
  department: string | null;
}

/**
 * A Users-tab row: a Core account (`row_type: 'user'`), or a Teamwork person with
 * no Core account (`'teamwork_only'`: `id` is null and the Core fields are empty).
 */
export interface AdminUser {
  id: string | null;
  row_type: 'user' | 'teamwork_only';
  email: string | null;
  display_name: string | null;
  full_name: string | null;
  platform_role: string | null;
  is_active: boolean | null;
  workspace_count: number;
  created_at: string | null;
  /** null renders "—": no Teamwork person has this email. */
  internal_member: InternalMember | null;
}

export interface AdminWorkspace {
  id: string;
  name: string;
  slug: string;
  website: string | null;
  is_active: boolean;
  member_count: number;
  owner_email: string | null;
  product_package?: string | null;
  google_ads_product_tier?: number | null;
  /** active | paused | offboarded; null for a workspace with no website (no tenant). */
  client_status?: 'active' | 'paused' | 'offboarded' | null;
  created_at: string | null;
}

export interface AdminAuditEvent {
  id: string;
  user_id: string | null;
  workspace_id: string | null;
  event_type: string;
  ip_address: string | null;
  metadata: Record<string, unknown>;
  created_at: string | null;
  user_email: string | null;
}

export interface AdminUsersResponse {
  users: AdminUser[];
  total: number;
  page: number;
  page_size: number;
  /** false where the Teamwork members migration isn't applied: no member data. */
  internal_member_available?: boolean;
  department_available?: boolean;
  /** null when no Teamwork refresh ran for this request. */
  teamwork_ok?: boolean | null;
  last_synced_at?: string | null;
}

export interface AdminWorkspacesResponse {
  workspaces: AdminWorkspace[];
  total: number;
  page: number;
  page_size: number;
  /** all=true only: the list was cut at 5000 workspaces. */
  truncated?: boolean;
}

export interface AdminAuditLogResponse {
  events: AdminAuditEvent[];
  total: number;
  page: number;
  page_size: number;
}

export interface AdminAuditLogParams {
  page?: number;
  page_size?: number;
  event_type?: string;
  user_id?: string;
  workspace_id?: string;
  since?: string;
}

/**
 * List all users with workspace counts and their Teamwork internal member, then
 * Teamwork people with no Core account. Requires an elevated platform role.
 */
export async function fetchAdminUsers(params?: {
  page?: number;
  search?: string;
  status?: string;
  platform_role?: string;
  show?: 'all' | 'core' | 'teamwork_only';
  internal?: 'active' | 'inactive' | 'none';
}): Promise<AdminUsersResponse> {
  const response = await apiClient.get<AdminUsersResponse>('/auth/admin/users', { params });
  return response.data;
}

/**
 * List all workspaces with member counts. Requires an elevated platform role.
 */
export async function fetchAdminWorkspaces(params?: {
  page?: number;
  search?: string;
  /** Every workspace in one response (cap 5000), for the browser-side table. */
  all?: boolean;
}): Promise<AdminWorkspacesResponse> {
  const response = await apiClient.get<AdminWorkspacesResponse>('/auth/admin/workspaces', { params });
  return response.data;
}

/**
 * Paginated audit log with optional filters. Requires an elevated platform role.
 */
export async function fetchAdminAuditLog(
  params?: AdminAuditLogParams
): Promise<AdminAuditLogResponse> {
  const response = await apiClient.get<AdminAuditLogResponse>('/auth/admin/audit-log', { params });
  return response.data;
}

export type TeamworkMemberPatch = Partial<
  Pick<InternalMember, 'active' | 'display_name_override' | 'note'>
>;

/** Change active / display name / note for one member (only the sent fields). */
export async function patchTeamworkMember(
  id: string,
  body: TeamworkMemberPatch,
): Promise<InternalMember> {
  const response = await apiClient.patch<InternalMember>(
    `/auth/admin/teamwork-members/${encodeURIComponent(id)}`,
    body,
  );
  return response.data;
}

/**
 * Promote or demote a user's platform role. Only platform_owner can do this.
 */
export async function setUserPlatformRole(
  userId: string,
  role: string
): Promise<{ user_id: string; old_role: string; new_role: string }> {
  const response = await apiClient.post<{ user_id: string; old_role: string; new_role: string }>(
    `/auth/admin/users/${userId}/platform-role`,
    { role }
  );
  return response.data;
}

/**
 * Assign a tenant's product package (seo / ads / both). Requires elevated platform role.
 */
export async function setTenantPackage(
  tenantId: string,
  productPackage: 'seo' | 'ads' | 'both'
): Promise<{ tenant_id: string; product_package: string }> {
  const response = await apiClient.patch<{ tenant_id: string; product_package: string }>(
    '/auth/admin/tenant-package',
    { product_package: productPackage },
    { params: { tenant_id: tenantId } }
  );
  return response.data;
}

/**
 * Assign a tenant's product tier (DTC=1 / B2B=2 / Enterprise=3). Requires elevated platform role.
 */
export async function setTenantTier(
  tenantId: string,
  productTier: number
): Promise<{ tenant_id: string; product_tier: number }> {
  const response = await apiClient.patch<{ tenant_id: string; product_tier: number }>(
    '/auth/admin/tenant-tier',
    { product_tier: productTier },
    { params: { tenant_id: tenantId } }
  );
  return response.data;
}

/**
 * Create a one-time admin invite link. Only platform_owner can do this.
 */
export async function createAdminInvite(
  role: string,
  toEmail?: string
): Promise<{ token: string; invite_link: string; expires_at: string; role: string; email_sent: boolean }> {
  const response = await apiClient.post<{ token: string; invite_link: string; expires_at: string; role: string; email_sent: boolean }>(
    '/auth/admin/invite',
    { role, to_email: toEmail }
  );
  return response.data;
}

/**
 * Validate an admin invite token (public — no auth required).
 */
export async function getAdminInviteInfo(
  token: string
): Promise<{ role: string; expires_at: string }> {
  const response = await apiClient.get<{ role: string; expires_at: string }>(
    `/auth/admin-invite/${token}`
  );
  return response.data;
}

/**
 * Claim an admin invite token — promotes the authenticated user to the invite role.
 */
export async function claimAdminInvite(
  token: string
): Promise<{ platform_role: string }> {
  const response = await apiClient.post<{ platform_role: string }>(
    `/auth/admin-invite/${token}/claim`
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Passkey / WebAuthn types and functions
// ---------------------------------------------------------------------------

export interface PasskeyInfo {
  credential_id: string;
  device_name: string | null;
  created_at: string;
  last_used_at: string | null;
  backed_up: boolean;
}

export interface PasskeyAuthResult {
  success: boolean;
  email: string;
  hashed_token: string | null;
  message: string;
}

/**
 * Begin passkey registration — returns WebAuthn PublicKeyCredentialCreationOptions.
 */
export async function passkeyRegisterBegin(): Promise<PublicKeyCredentialCreationOptionsJSON> {
  const response = await apiClient.post<PublicKeyCredentialCreationOptionsJSON>(
    '/auth/passkeys/register/begin'
  );
  return response.data;
}

/**
 * Complete passkey registration by submitting the browser's credential response.
 */
export async function passkeyRegisterComplete(
  credential: RegistrationResponseJSON,
  deviceName?: string
): Promise<void> {
  await apiClient.post('/auth/passkeys/register/complete', { credential, device_name: deviceName });
}

/**
 * List all passkeys registered for the authenticated user.
 */
export async function listPasskeys(): Promise<PasskeyInfo[]> {
  const response = await apiClient.get<PasskeyInfo[]>('/auth/passkeys/');
  return response.data;
}

/**
 * Delete a passkey by its base64url-encoded credential ID.
 */
export async function deletePasskey(credentialId: string): Promise<void> {
  await apiClient.delete(`/auth/passkeys/${encodeURIComponent(credentialId)}`);
}

/**
 * Begin passkey authentication — returns WebAuthn PublicKeyCredentialRequestOptions.
 */
export async function passkeyAuthBegin(email: string): Promise<PublicKeyCredentialRequestOptionsJSON> {
  const response = await apiClient.post<PublicKeyCredentialRequestOptionsJSON>(
    '/auth/passkeys/authenticate/begin',
    { email }
  );
  return response.data;
}

/**
 * Complete passkey authentication by submitting the browser's assertion response.
 */
export async function passkeyAuthComplete(
  email: string,
  credential: AuthenticationResponseJSON
): Promise<PasskeyAuthResult> {
  const response = await apiClient.post<PasskeyAuthResult>(
    '/auth/passkeys/authenticate/complete',
    { email, credential }
  );
  return response.data;
}

// TypeScript types from @simplewebauthn/browser (re-declared here to avoid importing the
// full library into this service file — the actual calls happen in the UI components).
type PublicKeyCredentialCreationOptionsJSON = Record<string, unknown>;
type PublicKeyCredentialRequestOptionsJSON = Record<string, unknown>;
type RegistrationResponseJSON = Record<string, unknown>;
type AuthenticationResponseJSON = Record<string, unknown>;

// ---------------------------------------------------------------------------
// Workspace administration (O58): create, edit, deactivate, member roles, remove, transfer ownership
// ---------------------------------------------------------------------------

/** Platform admins: a new workspace with NO owner and no members. */
export async function createAdminWorkspace(body: {
  name: string;
  website: string;
  package?: 'seo' | 'ads' | 'both';
}): Promise<AdminWorkspace> {
  const response = await apiClient.post<AdminWorkspace>('/auth/admin/workspaces', body);
  return response.data;
}

/**
 * The members of a workspace with e-mail, names and active flag, for the admin drawer. Platform admins, and
 * the owner/admin of that workspace; works for a deactivated workspace. (The shared members route returns ids.)
 */
export async function fetchAdminWorkspaceMembers(workspaceId: string): Promise<WorkspaceMember[]> {
  const response = await apiClient.get<WorkspaceMember[]>(`/auth/admin/workspaces/${workspaceId}/members`);
  return response.data;
}

/** Name and slug only: the website is the client's ID and cannot be changed. */
export async function updateWorkspace(
  workspaceId: string,
  body: { name?: string; slug?: string },
): Promise<AdminWorkspace> {
  const response = await apiClient.patch<AdminWorkspace>(`/auth/workspaces/${workspaceId}`, body);
  return response.data;
}

export async function setWorkspaceActive(
  workspaceId: string,
  active: boolean,
): Promise<{ id: string; is_active: boolean; changed: boolean; cache_invalidated?: boolean }> {
  const response = await apiClient.post(
    `/auth/workspaces/${workspaceId}/${active ? 'reactivate' : 'deactivate'}`,
  );
  return response.data;
}

/** admin | editor | viewer. Ownership moves only through transferWorkspaceOwnership. */
export async function updateMemberRole(
  workspaceId: string,
  userId: string,
  role: 'admin' | 'editor' | 'viewer',
): Promise<{ user_id: string; role: string; changed: boolean; cache_invalidated?: boolean }> {
  const response = await apiClient.patch(`/auth/workspaces/${workspaceId}/members/${userId}`, { role });
  return response.data;
}

export async function removeWorkspaceMember(
  workspaceId: string,
  userId: string,
): Promise<{ removed: boolean; user_id: string; cache_invalidated?: boolean }> {
  const response = await apiClient.delete(`/auth/workspaces/${workspaceId}/members/${userId}`);
  return response.data;
}

/** The target becomes the owner and the previous owner becomes admin, in one step. */
export async function transferWorkspaceOwnership(
  workspaceId: string,
  userId: string,
): Promise<{ workspace_id: string; owner_id: string; previous_owner_ids: string[]; cache_invalidated?: boolean }> {
  const response = await apiClient.post(`/auth/workspaces/${workspaceId}/transfer-ownership`, {
    user_id: userId,
  });
  return response.data;
}
